// src/pages/admin/Settings.tsx
import React, { useState, useEffect } from 'react';
import { AlertCircle, Check } from 'lucide-react';
import AdminHeader from '../../components/admin/common/AdminHeader';
import AdminBreadcrumb from '../../components/admin/common/AdminBreadcrumb';
import SettingsForm from '../../components/admin/forms/SettingsForm';
import { settingsService, type SiteSettings } from '../../services/settingsService';

const Settings: React.FC = () => {
  const [settings, setSettings] = useState<SiteSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const data = await settingsService.getSettings();
        setSettings(data);
      } catch (err) {
        setError('Failed to load settings');
        console.error('Error loading settings:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handleSubmit = async (data: SiteSettings) => {
    try {
      setLoading(true);
      setError(null);
      await settingsService.updateSettings(data);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError('Failed to update settings');
      console.error('Error updating settings:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !settings) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/4 mb-4" />
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-12 bg-gray-200 rounded" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <AdminHeader title="Settings" />

      <div className="max-w-4xl mx-auto px-6">
        <AdminBreadcrumb items={[{ label: 'Settings' }]} />

        {error && (
          <div className="mb-6 flex items-center gap-2 text-red-600 bg-red-50 p-4 rounded-lg">
            <AlertCircle className="w-5 h-5" />
            <p>{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-6 flex items-center gap-2 text-green-600 bg-green-50 p-4 rounded-lg">
            <Check className="w-5 h-5" />
            <p>Settings updated successfully</p>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6">
            <SettingsForm
              initialData={settings || undefined}
              onSubmit={handleSubmit}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;